// Soft signup prompt. Bottom sheet that appears at a "value moment" — after
// the guest creates their first habit, or after their first briefing. Fully
// dismissible. Never nags: once shown and closed, the shared onboarding flag
// suppresses further prompts on this device.

import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { sfx } from '../lib/sfx.js';

const COPY = {
  habit: {
    eyebrow: 'Nice start',
    title: 'Save your progress?',
    body: 'Create an account so your habits, streaks, and agendas stay safe and sync across every device you use.',
    cta: 'Save my progress',
  },
  briefing: {
    eyebrow: 'Nice agenda',
    title: 'Keep it across devices?',
    body: 'Sign up to sync your agendas, streaks, and notes. Works the same on iPhone, iPad, and the web.',
    cta: 'Sign up to sync',
  },
};

export function SaveProgressSheet({ open, variant = 'habit', onSignUp, onDismiss }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Tick twice for a clean enter animation.
      requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      sfx.sheetOpen && sfx.sheetOpen();
    } else {
      setShown(false);
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!mounted) return null;

  const copy = COPY[variant] || COPY.habit;

  const close = () => { sfx.sheetClose && sfx.sheetClose(); onDismiss && onDismiss(); };
  const accept = () => { sfx.toast && sfx.toast(); onSignUp && onSignUp(); };

  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 180,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        pointerEvents: shown ? 'auto' : 'none',
      }}
    >
      {/* Scrim */}
      <div
        onClick={close}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          opacity: shown ? 1 : 0,
          transition: 'opacity 0.22s ease',
        }}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'relative',
          margin: '0 10px 10px',
          padding: 22,
          borderRadius: 24,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.22), transparent 55%), rgba(19,21,25,0.94)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--stroke-1)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
          transform: shown ? 'translateY(0)' : 'translateY(24px)',
          opacity: shown ? 1 : 0,
          transition: 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease',
        }}
      >
        {/* Drag pill */}
        <div style={{
          width: 40, height: 4, borderRadius: 999,
          background: 'rgba(255,255,255,0.16)',
          margin: '0 auto 16px',
        }}/>

        <div style={{
          width: 48, height: 48, borderRadius: 14,
          background: 'var(--grad-cta)',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 0 20px rgba(45,156,255,0.5), inset 0 1px 0 rgba(255,255,255,0.3)',
          marginBottom: 14,
        }}>
          <Icon name="lock" size={22} color="#fff" stroke={2}/>
        </div>

        <div className="eyebrow" style={{ fontSize: 9, marginBottom: 6 }}>
          {copy.eyebrow}
        </div>
        <h2 style={{
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 22,
          letterSpacing: '-0.02em', color: 'var(--fg-1)', margin: 0, marginBottom: 8,
        }}>
          {copy.title}
        </h2>
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.5,
          color: 'var(--fg-2)', margin: 0, marginBottom: 20,
        }}>
          {copy.body}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={accept}
            className="btn btn-primary"
            style={{ padding: '12px 18px', borderRadius: 14, fontSize: 14 }}
          >
            {copy.cta}
          </button>
          <button
            onClick={close}
            style={{
              padding: '10px 18px', borderRadius: 14,
              background: 'transparent', border: 'none',
              color: 'var(--fg-3)', cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13,
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
